import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChange } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { User, usernameAsString } from '../../models/user';
import { isGuestUser } from '../../models/guest-user';
import { AppService } from '../../services/app.service';
import { AuthenticationService } from '../../services/authentication.service';
import { CustomerService } from '../../services/customer.service';
import { ErrorService } from '../../services/error.service';
import { OrderService } from '../../services/order.service';
import { SnackbarService } from '../../services/snackbar.service';
import { UserService } from '../../services/user.service';
import { ModalComponent } from '../modal/modal.component';
import { WhatsNewModalComponent } from '../whats-new-modal/whats-new-modal.component';
import { CustomPageService } from "../../services/custom-page.service";
import { ICustomOverridePage, OverridePages } from "../../models/pages";
import { environment } from 'src/environments/environment';
import { CustomizationService } from 'src/app/services/customization.service';
import { MfaChallengeService } from '../../services/mfa-challenge.service';
import { MfaChallengeComponent, MfaChallengeDialogData, MfaChallengeResult } from '../mfa-challenge/mfa-challenge.component';
import { MfaCustomerSwitchEvaluation } from '../../models/mfa';

declare const $: any;

@Component({
	templateUrl: './sidebar.html',
	selector: 'app-sidebar',
	styleUrls: ['./sidebar.scss']
})

export class SidebarComponent implements OnInit, OnDestroy, OnChanges {
	public companyLogo: string;
	public currentCustomerId: any;
	public currentDriver: any;
	public currentDriverId: number;
	public customerSubscription: any;
	public logoSubscription: any;
	public selectedCustomer: any;
	@Input() public sidebarCollapsed: any;
	@Input() public toggleSidebarCollapsed: any;
	public user = new User();
	// XD-62456: a guest sees Place Order only, whatever its permission flags say. Pre-computed (no template calls).
	public isGuest = false;
	public userSubscription: any;
	public subscriptionsUnsubscribe = new Subject<void>();
	public pageKeys: any;
	public overridePage: ICustomOverridePage;

	constructor(private _userService: UserService,
				public _router: Router,
				private _dialog: MatDialog,
				private _errorService: ErrorService,
				private _orderService: OrderService,
				private _customerService: CustomerService,
				private _authenticationService: AuthenticationService,
				private _appService: AppService,
				private _snackbarService: SnackbarService,
				private _customPageService: CustomPageService,
				private _customizationService: CustomizationService,
				private _mfaChallengeService: MfaChallengeService) {

	}

	clearTemplateBookmark() {
		localStorage.removeItem('templateCriteria');
		localStorage.removeItem('templatePageNum');
	}

	initTooltips() {
		$(document).tooltip({
			position: {
				my: 'left+10 center',
				at: 'right center',
			},
			disabled: !this.sidebarCollapsed
		});
	}

	logout() {
		// HomeComponent's params subscription reads the `logout` matrix param
		// and triggers AuthenticationService.logout().
		this._router.navigate(['/home', {logout: true}]);
	}

	ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
		if (changes.sidebarCollapsed && !changes.sidebarCollapsed.firstChange) {
			if (changes.sidebarCollapsed.currentValue === true) {
				this.initTooltips();
			} else {
				$(document).tooltip('destroy');
			}
		}
	}

	ngOnDestroy() {
		if (this.sidebarCollapsed === true) {
			$(document).tooltip('destroy');
		}

		if (this.userSubscription) {
			this.userSubscription.unsubscribe();
		}

		if (this.logoSubscription) {
			this.logoSubscription.unsubscribe();
		}

		if (this.customerSubscription) {
			this.customerSubscription.unsubscribe();
		}
		this.subscriptionsUnsubscribe.next();
	}

	ngOnInit() {
		this._customizationService.getRapidShipCustomPages(environment.siteId).subscribe({
			next: (customPages: any) => {
				localStorage.setItem('Rapidship:customPages', JSON.stringify(customPages));
				this.pageKeys = this._customPageService.getStoredPages().customPages
      				.sort((a, b) => a.name > b.name ? 1 : (a.name === b.name ? 0 : -1));
				this.overridePage = this._customPageService.getOverridePage(OverridePages.PLACE_ORDER);
			}, error: (error) => {
				this._errorService.error(error, 'getRapidShipCustomPages failed', 'modal');
			}
		});
 		this.userSubscription = this._userService.user$.subscribe((data) => {
			this.user = data;
			this.isGuest = isGuestUser(this.user);
			if (this.user && this.user.type === 'worker' && this.user.driverId) {
				this.currentDriverId = this.user.driverId;
				this.currentDriver = this.user.associatedDrivers.find((ad) => ad.id === this.currentDriverId);
			}
		});
		this.logoSubscription = this._appService.companyLogo$.subscribe(response => {
			this.companyLogo = response;
		});
		this.selectedCustomer = this._customerService.getSelectedCustomer();
		this.currentCustomerId = this.selectedCustomer ? this.selectedCustomer.id : null;
		this.customerSubscription = this._customerService.selectedCustomer$.subscribe(data => {
			if (data) {
				this.selectedCustomer = data;
				this.currentCustomerId = this.selectedCustomer ? this.selectedCustomer.id : null;
				if (this.selectedCustomer.billingStatus === 'PAST_DUE') {
					const dialogConfig: MatDialogConfig = {
						disableClose: true,
						data: {
							title: 'Warning',
							message: `Warning!  Your customer account has outstanding invoices that need attention.  Please contact our customer service department for more information.`,
							okText: 'Ok'
						}
					};
					this._dialog.open(ModalComponent, dialogConfig);
				}
			}
		});
		if (this.sidebarCollapsed) {
			this.initTooltips();
		}
		this._authenticationService.showWhatsNew$.pipe(takeUntil(this.subscriptionsUnsubscribe)).subscribe((response) => {
			if (response) {
				this.showWhatsNew();
			}
		});
	}

	reloadCurrentRoute() {
		const currentUrl = this._router.url;
		this._router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
			this._router.navigate([currentUrl]);
		});
	}

	setSelectedCustomer() {
		if (this._router.url === '/place-order') {
			const dialogRef = this._dialog.open(ModalComponent, {
				data: {
					title: 'Warning',
					message: 'You are about to switch customer accounts on this order which will reset the order. Would you like to proceed?',
					showCancel: true,
					cancelText: 'No',
					okText: 'Yes'
				}
			});
			dialogRef.afterClosed().subscribe((result) => {
				if (result) {
					this.updateCustomer();
					this._orderService.resetCurrentOrder();
				} else {
					this.currentCustomerId = this.selectedCustomer ? this.selectedCustomer.id : null;
					return;
				}
			});
		} else {
			this.updateCustomer();
		}
	}

	setSelectedDriver() {
		const user = Object.assign({}, this.user, {driverId: this.currentDriverId});
		this._userService.updateUser(user);
	}

	showWhatsNew() {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = false;
		dialogConfig.width = '600px';
		this._dialog.open(WhatsNewModalComponent, dialogConfig);
	}

	updateCustomer() {
		// Capture the route before anything changes it — reloadCurrentRoute() re-navigates and the
		// order-confirmation branch below routes away, so _router.url is not stable past this point.
		const wasOnOrderConfirmation = this._router.url.startsWith('/order-confirmation');
		const sc = this.user.allowedCustomers.filter(customer => customer.id == this.currentCustomerId)[0];

		if (this._mfaChallengeService.isCustomerSwitchCompleted(sc.id)) {
			this.performCustomerSwitch(sc.id, wasOnOrderConfirmation);
			return;
		}

		const mfaToken = this._mfaChallengeService.getMfaToken(
			usernameAsString(this.user),
			sc.id
		) || this._mfaChallengeService.getMfaToken(usernameAsString(this.user));

		this._mfaChallengeService.evaluateCustomerSwitch(sc.id, mfaToken)
			.pipe(takeUntil(this.subscriptionsUnsubscribe))
			.subscribe({
				next: (evaluation) => {
					if (evaluation.challengeRequired) {
						this.openCustomerSwitchChallenge(sc.id, evaluation, wasOnOrderConfirmation);
					} else {
						this._mfaChallengeService.markCustomerSwitchCompleted(sc.id);
						this.performCustomerSwitch(sc.id, wasOnOrderConfirmation);
					}
				},
				error: (error) => {
					// Fail closed: an evaluation failure must not bypass the MFA step-up gate.
					// Revert the dropdown to the active customer and surface the error instead of switching.
					this.currentCustomerId = this.selectedCustomer ? this.selectedCustomer.id : null;
					this._errorService.error(error, 'Unable to verify MFA requirements for this customer. Please try again.', 'modal');
				}
			});
	}

	private openCustomerSwitchChallenge(custId: number, evaluation: MfaCustomerSwitchEvaluation, wasOnOrderConfirmation: boolean): void {
		const dialogData: MfaChallengeDialogData = {
			challengeSessionId: evaluation.challengeSessionId,
			preferredMethod: evaluation.preferredMethod,
			availableMethods: evaluation.availableMethods || [],
			maskedEmail: evaluation.maskedEmail,
			otpTtlMinutes: evaluation.otpTtlMinutes,
			otpResendDelaySeconds: evaluation.otpResendDelaySeconds,
			maxAttempts: evaluation.maxAttempts,
			custId: custId
		};

		const dialogRef = this._dialog.open(MfaChallengeComponent, {
			width: '500px',
			maxWidth: '90vw',
			disableClose: true,
			data: dialogData
		});

		dialogRef.afterClosed()
			.pipe(takeUntil(this.subscriptionsUnsubscribe))
			.subscribe((result: MfaChallengeResult) => {
				if (result?.success) {
					if (result.mfaSessionToken) {
						this._mfaChallengeService.storeMfaToken(
							usernameAsString(this.user),
							result.mfaSessionToken,
							custId
						);
					}
					// An Email OTP challenge re-proves the address (the backend flips emailVerified back on),
					// so refresh the cached flag — the customer-switch challenge shares the login verify path.
					if (result.method === 'EmailOtp' && this.user && !this.user.emailVerified) {
						this.user.emailVerified = true;
						this._userService.updateUser(this.user);
					}
					this._mfaChallengeService.markCustomerSwitchCompleted(custId);
					this.performCustomerSwitch(custId, wasOnOrderConfirmation);
				} else {
					this.currentCustomerId = this.selectedCustomer ? this.selectedCustomer.id : null;
				}
			});
	}

	private performCustomerSwitch(custId: number, wasOnOrderConfirmation: boolean): void {
		this._customerService.getCustomer(custId).subscribe({
			next: (selectedCustomer) => {
				this._customerService.updateSelectedCustomer(selectedCustomer);
				this._snackbarService.openSnackBar(`Current customer set to ${selectedCustomer.id} - ${selectedCustomer.customerName}`, 'Close', 3500, 'success');

				// Leaving Order Confirmation is not enough on its own: that page re-displays the PREVIOUS
				// customer's order from the remembered id, and its Edit Order card loads that order whatever
				// customer is selected. Drop the id before branching — the credit-hold redirect below also
				// leaves the page, and must not skip the cleanup.
				if (wasOnOrderConfirmation) {
					this._orderService.setLastPlacedOrderId(null);
				}

				if (selectedCustomer.billingStatus === 'CREDIT_HOLD' && this.user?.globalSettings?.displayInvoices && this.user?.inetUser?.viewInvoices) {
					const qbiMessage = `Warning!  Your customer account is on credit hold due to outstanding invoices that need immediate attention.  All invoices that have reached your ${selectedCustomer?.startCreditHoldEnforcementDay} aging limit must be paid in order to proceed with order placement.  Please navigate to the Invoices page or contact our customer service department for more information.`;
					const nonQbiMessage = 'Warning!  Your customer account is on credit hold due to outstanding invoices that need immediate attention. Please navigate to the Invoices page or contact our customer service department for more information.';
					const dialogConfig: MatDialogConfig = {
						disableClose: true,
						data: {
							title: 'Warning',
							message: this.user?.globalSettings?.isQuickbooksEnabled ? qbiMessage : nonQbiMessage,
							okText: 'Ok'
						}
					};
					this._dialog.open(ModalComponent, dialogConfig).afterClosed().subscribe(() => {
						this._router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
							this._router.navigate(['invoices']);
						});
					});
				} else if (wasOnOrderConfirmation) {
					this._router.navigate(['place-order']);
				} else {
					this.reloadCurrentRoute();
				}
			},
			error: (error) => {
				this._errorService.error(error, 'getCustomer failed', 'modal');
			}
		});
	}
}
